import { useCallback, useState } from 'react';

function readStoredValue<T>(key: string, defaultValue: T): T {
  if (typeof window === 'undefined') return defaultValue;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return defaultValue;
    const parsed: unknown = JSON.parse(raw);
    return parsed !== null && typeof parsed === typeof defaultValue ? (parsed as T) : defaultValue;
  } catch {
    return defaultValue;
  }
}

/** State kept in this browser's localStorage; falls back to `defaultValue` when storage is empty, invalid or blocked. */
export function useLocalStorage<T>(key: string, defaultValue: T): [T, (next: T) => void] {
  const [value, setValue] = useState(() => readStoredValue(key, defaultValue));

  const setStoredValue = useCallback(
    (next: T) => {
      setValue(next);
      try {
        window.localStorage.setItem(key, JSON.stringify(next));
      } catch {
        // Storage blocked or full: the value still holds for this session
      }
    },
    [key],
  );

  return [value, setStoredValue];
}
